import normalizeRGB from './normalize-rgb';
import RGBColor from '../classes/rgb-color';
import converters from '../converters';
import sRGBAssume from './srgb-assume';

const blendColors = (backgroundColor, foregroundColor) => {
  const { red, green, blue, alpha: foregroundColorAlpha } = foregroundColor;
  const { red: bgRed, blue: bgBlue, green: bgGreen, alpha: backgroundColorAlpha } = backgroundColor;
  const RGBValues = [red, green, blue].map((value) => value * 255);
  const backgroundColorRGBValues = [bgRed, bgGreen, bgBlue].map((value) => value * 255);

  if (foregroundColorAlpha >= 1) {
    return foregroundColor;
  }

  if (foregroundColorAlpha === 0) {
    return backgroundColor;
  }
  const [blendRed, blendGreen, blendBlue] = RGBValues.map((rgbValue, index) => {
    const blendedRgbValue =
      rgbValue * foregroundColorAlpha +
      backgroundColorRGBValues[index] * backgroundColorAlpha * (1 - foregroundColorAlpha);
    return Math.round(blendedRgbValue * 1000) / 1000;
  });

  const blendAlpha = foregroundColorAlpha + backgroundColorAlpha * (1 - foregroundColorAlpha);

  return new RGBColor(blendRed / 255, blendGreen / 255, blendBlue / 255, blendAlpha);
};

const getLuminance = (rgbColor) => {
  // Formula: http://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
  const { red, green, blue } = rgbColor;
  return 0.2126 * sRGBAssume(red) + 0.7152 * sRGBAssume(green) + 0.0722 * sRGBAssume(blue);
};

const contrastRatio = (
  backgroundHSLColor,
  foregroundHSLColor,
  options = {
    isRGB: false,
    surfaceColor: null,
  }
) => {
  const backgroundRGB = options.isRGB
    ? backgroundHSLColor
    : converters.hsl2rgb(backgroundHSLColor).round();
  let foregroundRGB = options.isRGB
    ? foregroundHSLColor
    : converters.hsl2rgb(foregroundHSLColor).round();

  const { alpha: backgroundAlpha } = backgroundRGB;

  if (backgroundAlpha >= 1) {
    if (foregroundRGB.alpha < 1) {
      foregroundRGB = blendColors(backgroundRGB, foregroundRGB);
    }

    const backgroundLuminance = getLuminance(normalizeRGB(backgroundRGB)) + 0.05;
    const foregroundLuminance = getLuminance(normalizeRGB(foregroundRGB)) + 0.05;
    const ratio =
      foregroundLuminance > backgroundLuminance
        ? 1 / (backgroundLuminance / foregroundLuminance)
        : backgroundLuminance / foregroundLuminance;

    return Math.round(ratio * 1000) / 1000;
  }
  // Semi-transparent background
  // The text color may or may not be semi-transparent, but that doesn't matter

  if (options.surfaceColor) {
    const surfaceColorRGB = converters.hsl2rgb(options.surfaceColor).round();
    const onSurfaceColor = blendColors(surfaceColorRGB, backgroundRGB);
    return contrastRatio(onSurfaceColor, foregroundRGB, { isRGB: true });
  }

  const white = new RGBColor(1, 1, 1);
  const black = new RGBColor(0, 0, 0);
  const onBlack = blendColors(black, backgroundRGB);
  const onWhite = blendColors(white, backgroundRGB);
  const contrastOnBlack = contrastRatio(onBlack, foregroundRGB, { isRGB: true });
  const contrastOnWhite = contrastRatio(onWhite, foregroundRGB, { isRGB: true });

  const max = Math.max(contrastOnBlack, contrastOnWhite);

  const foregroundLuminance = getLuminance(normalizeRGB(foregroundRGB));
  const onBlackLuminance = getLuminance(normalizeRGB(onBlack));
  const onWhiteLuminance = getLuminance(normalizeRGB(onWhite));

  let min = 1;
  if (onBlackLuminance > foregroundLuminance) {
    min = contrastOnBlack;
  } else if (onWhiteLuminance < foregroundLuminance) {
    min = contrastOnWhite;
  }

  const ratio = (min + max) / 2;

  return Math.round(ratio * 1000) / 1000;
};

export default contrastRatio;
